import React from 'react';
import { connect } from 'react-redux';
import { HttpStateLine } from 'state-line';

import Form from 'xgimi-ui/Form';
import InfoFile, { InfoFileType } from 'xgimi-ui/Info/File';
import Dialog from 'xgimi-ui/Dialog';
import StoreComponent, { mapStateToProps } from '@/appComponents/Store';
import ButtonBase from 'xgimi-ui/mui/material/ButtonBase';
import CircularProgress from 'xgimi-ui/mui/material/CircularProgress';
import FileViewer from 'xgimi-ui/FileViewer';
import images from '@/assets/images/index';
import Confirm from '@/components/Confirm';
import Api from '@/api';

import s from './.module.scss';

interface Props {
  // 最大限制
  maxLimit?: number;
  // 上传文件类型
  type: Array<'Image' | 'Video'>;
  // 是否是更新
  isUpdate?: boolean;
  /**
   * 数据更新
   * @param data URL列表
   */
  onUpdate?: (data: string | string[]) => void;
  // 默认数据
  defaultValue?: string | string[];
}

interface State {
  // 图片列表
  imageList: string | string[];
}

/**
 * 图片/视频上传组件
 */
class Component extends StoreComponent<Props, State> {
  static defaultProps: Props = {
    maxLimit: 100,
    type: ['Image'],
    isUpdate: true,
  };

  state: State = {
    imageList: [],
  };

  // 对话框
  private dialog: React.RefObject<Dialog & HTMLDivElement> = React.createRef();
  componentDidMount() {
    if (this.props.defaultValue) this.setState({ imageList: this.props.defaultValue });
  }

  private openError = (): void => {
    this.storeState.common.dialog.current?.open(
      <div className={s.dialogContainer}>
        <div className={s.error}>上传失败，单次上传图片/视频大小<br/>请不要超过50M，个数不超过3个。</div>
        <div className={s.onAffirm} onClick={() => this.storeState.common.dialog.current?.close()}>确定</div>
      </div>,
      {
        classes: { paper: s.dialogPaper },
      },
    );
  };

  /**
   * 渲染视频
   * @param src
   */
  private renderVideo = (src: string): React.ReactNode => (
    <div
      onClick={() => {
      this.dialog.current?.open(
        <video
          className={s.showVideoMobile}
          src={src}
          controls={true}
          poster={'https://file02.xgimi.com//inner-purchase/13cecbae376a40e1b8771ee3a8aadd12.png'}
        >
          <track kind={'captions'} />
        </video>, {
          style: {
            lineHeight: 0,
          },
        }
      );
      }}
    >
      <video
        className={s.fileVideo}
        src={src}
        controls={false}
        poster={'https://file02.xgimi.com//inner-purchase/13cecbae376a40e1b8771ee3a8aadd12.png'}
      >
        <track kind={'captions'} />
      </video>
      <img src={images.play} alt="" width={28} height={28} className={s.player}/>
    </div>
  );

  /**
   * 渲染图片
   * @param src
   * @param index
   */
  private renderImage = (src: string, index?: number): React.ReactNode => (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <img
      key={`${index}_${src}`}
      className={s.fileImage}
      src={src}
      alt={src}
      onClick={() => {
        this.dialog.current?.open(
          <img
            className={s.showImageMobile}
            src={src}
            alt={src}
          />, {
            style: {
              lineHeight: 0,
            },
          }
        );
      }}
    />
  );

  render(): React.ReactNode {
    return (
      <div className={s.container}>
        <Form className={s.uploadForm}>
          <InfoFile
            data={this.state.imageList}
            onError={() => {
              this.openError();
              return false;
            }}
            isUpdate={this.props.isUpdate}
            className={s.images}
            maxSize={1024 * 50}
            mimes={
              (this.props.type.includes('Image') && this.props.type.includes('Video'))
                ? ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/svg+xml', 'image/x-icon',
                  'audio/3gpp', 'video/3gpp', 'audio/mp4', 'video/mp4', 'audio/3gpp', 'video/3gpp', 'video/quicktime']
                : this.props.type.includes('Image')
                  ? ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/svg+xml', 'image/x-icon']
                  : ['audio/3gpp', 'video/3gpp', 'audio/mp4', 'video/mp4', 'audio/3gpp', 'video/3gpp', 'video/quicktime']
            }
            maxLimit={this.props.maxLimit}
            classes={{ image: s.fileImage, video: s.fileVideo }}
            onUpdate={(data) => {
              this.setState({ imageList: data });
              this.props.onUpdate?.(data);
            }}
            renderUploadButton={(onStartUpload) => (this.state.imageList.length < this.props.maxLimit!
              ? (
                <ButtonBase
                  component={'span'}
                  classes={{ root: s.uploadEmpty }}
                  disableRipple={true}
                  disableTouchRipple={true}
                  onClick={(e) => {
                    e.preventDefault();
                    this.dialog.current?.open(
                      <Confirm
                        confirmText="同意"
                        cancelText="不同意"
                        onConfirm={() => {
                          onStartUpload();
                          this.dialog.current?.close();
                        }}
                        onCancel={() => this.dialog.current?.close()}
                      >
                        &quot;极米商城&quot;想访问你的照片图库<br/>
                        开启后您可在评论、售后、反馈意见时上传图片和视频
                      </Confirm>
                    );
                  }}
                >
                  <img src={images.iconImage} alt="+" style={{ width: '32px', height: '32px' }}/>
                </ButtonBase>
              )
              : <></>)}
            onUpload={(file: any) => new Promise<string | null>(async (resolve, reject) => {
              const httpStateLine = await new HttpStateLine(new Api.common.UploadFile({
                file,
              })).run().catch(() => null);
              if (httpStateLine) resolve(httpStateLine.result.data);
              reject(null);
            })}
            renderDownloadButton={(onClick) => <div className={s.download} onClick={onClick}><img src={images.picVideo} alt="download"/></div>}
            renderLoading={() => (
              <div className={s.loadingMargin}>
                <CircularProgress/>
              </div>
            )}
            render={(data, uploadButton, isUpdate, input) => {
              const list: React.ReactNode[] = [];
              data.forEach((item: string, index: number) => {
                // 依据文件后缀渲染
                switch (FileViewer.getFileTypeByFileSrc(item)) {
                  case InfoFileType.Image:
                    list.push(this.renderImage(item, index));
                    return;
                  case InfoFileType.Video:
                    list.push(this.renderVideo(item));
                }
              });
              return (
                <div className={s.list}>
                  {list.map((item: React.ReactNode, index: number) => (
                    <div className={s.item} key={`${index}_${data[index]}`}>
                      {isUpdate && (
                        <div
                          className={s.deleteButton}
                          onClick={() => {
                            input(data.filter((filePath: string, filePathIndex: number) => filePathIndex !== index));
                          }}
                        >
                          <img src={images.iconClose} alt="" width={20} height={20}/>
                        </div>
                      )}
                      {/* <div className={s.mask}/> */}
                      {item}
                    </div>
                  ))}
                  {uploadButton}
                </div>
              );
            }}
          />
        </Form>
        <Dialog
          ref={this.dialog}
          classes={{
            paper: s.dialogPaper,
          }}
          maxWidth={'lg'}
          scroll={'body'}
        />
      </div>
    );
  }
}

export default connect(mapStateToProps)(Component);
